import { BadgeVenta, VENTA_META } from '@/components/BadgeVenta';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useObras } from '@/hooks/useObras';
import { useProyectos } from '@/hooks/useProyectos';
import { cn } from '@/lib/utils';
import type { EstadoVenta, Obra } from '@adelante/shared';
import {
  Buildings,
  CaretRight,
  CircleNotch,
  Columns,
  GridFour,
  ListBullets,
  MagnifyingGlass,
  X,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { KanbanAvanceVista } from './KanbanAvanceVista';
import { MatrizAvanceVista } from './MatrizAvanceVista';

type Vista = 'lista' | 'matriz' | 'kanban';

/**
 * Pantalla de Campo — Selección y panorama de avance.
 *
 * Dos vistas sobre las obras habilitadas del proyecto:
 *   - Lista: cards táctiles agrupadas por bloque (mobile-friendly).
 *   - Matriz: grilla obras × partidas ("Por Costos").
 * Ambas hacen tap/click → pantalla de captura de la obra.
 */
export function CampoObrasPantalla() {
  const proyectosQuery = useProyectos();
  const proyectos = proyectosQuery.data?.data ?? [];

  // Vista y proyecto se recuerdan en sessionStorage para no perder la selección
  // al entrar a una casa y volver.
  const [proyectoSel, setProyectoSel] = useState<string | null>(
    () => sessionStorage.getItem('campo.proyecto') || null,
  );
  // Búsqueda y filtro de venta también se recuerdan en sessionStorage: al entrar
  // a una casa y volver, el filtro sigue puesto (pedido del equipo de campo).
  const [busqueda, setBusqueda] = useState(() => sessionStorage.getItem('campo.busqueda') || '');
  const [ventaFiltro, setVentaFiltro] = useState<EstadoVenta | null>(() => {
    const v = sessionStorage.getItem('campo.ventaFiltro');
    return v === 'formalizada' || v === 'reservada' || v === 'disponible' || v === 'entregada'
      ? v
      : null;
  });
  const [vista, setVista] = useState<Vista>(() => {
    const v = sessionStorage.getItem('campo.vista');
    return v === 'matriz' || v === 'kanban' ? v : 'lista';
  });

  useEffect(() => {
    if (proyectoSel) sessionStorage.setItem('campo.proyecto', proyectoSel);
  }, [proyectoSel]);
  useEffect(() => {
    sessionStorage.setItem('campo.vista', vista);
  }, [vista]);
  useEffect(() => {
    sessionStorage.setItem('campo.busqueda', busqueda);
  }, [busqueda]);
  useEffect(() => {
    if (ventaFiltro) sessionStorage.setItem('campo.ventaFiltro', ventaFiltro);
    else sessionStorage.removeItem('campo.ventaFiltro');
  }, [ventaFiltro]);

  // Auto-selecciona el primer proyecto cuando llegan (o "Todos" si fue elegido).
  const proyectoActivo =
    proyectoSel === '__TODOS__' ? null : (proyectoSel ?? proyectos[0]?.codigo ?? null);

  const obrasQuery = useObras(proyectoActivo ? { proyecto: proyectoActivo } : {});
  const obras = obrasQuery.data?.data ?? [];

  // Casas habilitadas para captura. Incluye congeladas (en_espera): se muestran
  // opacas pero no desaparecen — el equipo necesita ver su información.
  const habilitadas = useMemo(
    () =>
      obras.filter(
        (o) => o.estado_operativo === 'en_ejecucion' || o.estado_operativo === 'en_espera',
      ),
    [obras],
  );

  // Filtro de texto: código, número y modelo (sin acentos / mayúsculas).
  const obrasTexto = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return habilitadas;
    return habilitadas.filter((o) =>
      [o.codigo, o.numero_obra, o.nombre_modelo ?? '', o.tipo_casa ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [habilitadas, busqueda]);

  // Conteo por estado de venta (sobre el filtro de texto, NO el de venta) para
  // los chips. Así el usuario ve cuántas casas hay de cada estado.
  const ventaConteos = useMemo(() => {
    const conteos: Record<EstadoVenta, number> = {
      formalizada: 0,
      reservada: 0,
      disponible: 0,
      entregada: 0,
    };
    for (const o of obrasTexto) if (o.estado_venta) conteos[o.estado_venta]++;
    return { conteos, total: obrasTexto.length };
  }, [obrasTexto]);

  // Filtro final = texto + estado de venta.
  const obrasFiltradas = useMemo(
    () => (ventaFiltro ? obrasTexto.filter((o) => o.estado_venta === ventaFiltro) : obrasTexto),
    [obrasTexto, ventaFiltro],
  );

  const porBloque = useMemo(() => {
    const map = new Map<string, Obra[]>();
    for (const o of obrasFiltradas) {
      const k = o.bloque_letra;
      const arr = map.get(k) ?? [];
      arr.push(o);
      map.set(k, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [obrasFiltradas]);

  return (
    <div className="space-y-4">
      <div className="sticky top-12 z-30 -mx-4 flex items-start justify-between gap-3 border-b border-border bg-zinc-50/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-zinc-50/80">
        <div>
          <h1 className="text-xl font-semibold">Avance</h1>
          <p className="text-xs text-muted-foreground">
            Elegí la obra donde vas a registrar avance hoy.
          </p>
        </div>
        {/* Toggle Lista / Matriz / Kanban */}
        <div className="flex shrink-0 rounded-md border border-border bg-white p-0.5">
          <ToggleBtn
            label="Vista de lista"
            activo={vista === 'lista'}
            onClick={() => setVista('lista')}
          >
            <ListBullets className="h-4 w-4" weight="bold" aria-hidden="true" />
          </ToggleBtn>
          <ToggleBtn
            label="Vista Kanban"
            activo={vista === 'kanban'}
            onClick={() => setVista('kanban')}
          >
            <Columns className="h-4 w-4" weight="bold" aria-hidden="true" />
          </ToggleBtn>
          <ToggleBtn
            label="Vista de matriz"
            activo={vista === 'matriz'}
            onClick={() => setVista('matriz')}
          >
            <GridFour className="h-4 w-4" weight="bold" aria-hidden="true" />
          </ToggleBtn>
        </div>
      </div>

      {/* Chips de proyecto (incluye "Todos" — pensado especialmente para Kanban) */}
      <div className="-mx-4 overflow-x-auto px-4">
        <div className="flex gap-2">
          {(() => {
            const todosActivo = proyectoSel === '__TODOS__';
            return (
              <button
                type="button"
                onClick={() => setProyectoSel('__TODOS__')}
                className={cn(
                  'whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  todosActivo
                    ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                    : 'border-border bg-white text-muted-foreground',
                )}
              >
                Todos
              </button>
            );
          })()}
          {proyectos.map((p) => {
            const activo = p.codigo === proyectoActivo;
            return (
              <button
                key={p.codigo}
                type="button"
                onClick={() => setProyectoSel(p.codigo)}
                className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  activo
                    ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                    : 'border-border bg-white text-muted-foreground'
                }`}
              >
                {p.codigo}
              </button>
            );
          })}
        </div>
      </div>

      {vista === 'matriz' && <MatrizAvanceVista proyecto={proyectoActivo} />}
      {vista === 'kanban' && <KanbanAvanceVista proyecto={proyectoActivo} />}

      {/* (Lista abajo) */}
      {vista === 'lista' && (
        <>
          {/* Buscador de obra */}
          <div className="relative">
            <MagnifyingGlass
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              weight="bold"
              aria-hidden="true"
            />
            <Input
              type="search"
              inputMode="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar casa: código, número o modelo…"
              className="pl-9 pr-9"
              aria-label="Buscar obra"
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-zinc-100"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-4 w-4" weight="bold" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Filtro por estado de venta — compacto (solo la letra), pensado para móvil. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setVentaFiltro(null)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                ventaFiltro === null
                  ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                  : 'border-border bg-white text-muted-foreground',
              )}
            >
              Todas
              <span className="ml-1 tabular-nums opacity-70">{ventaConteos.total}</span>
            </button>
            {(['formalizada', 'reservada', 'disponible', 'entregada'] as const).map((ev) => {
              const m = VENTA_META[ev];
              const activo = ventaFiltro === ev;
              return (
                <button
                  key={ev}
                  type="button"
                  onClick={() => setVentaFiltro(activo ? null : ev)}
                  title={`${m.label} (${ventaConteos.conteos[ev]})`}
                  aria-label={`Filtrar ${m.label}`}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium transition-colors',
                    activo
                      ? 'border-adelante-primary bg-adelante-primary/10'
                      : 'border-border bg-white text-muted-foreground',
                  )}
                >
                  <BadgeVenta estado={ev} />
                  <span className="tabular-nums opacity-80">{ventaConteos.conteos[ev]}</span>
                </button>
              );
            })}
          </div>

          {obrasQuery.isLoading && (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando obras…
            </div>
          )}

          {!obrasQuery.isLoading && habilitadas.length === 0 && (
            <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No hay obras habilitadas en este proyecto. Pedile a un administrador que las habilite
              desde Obras.
            </p>
          )}

          {!obrasQuery.isLoading && habilitadas.length > 0 && obrasFiltradas.length === 0 && (
            <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Ninguna obra coincide con «{busqueda}».
            </p>
          )}

          {/* Obras agrupadas por bloque */}
          <div className="space-y-4">
            {porBloque.map(([bloque, lista]) => (
              <section key={bloque}>
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Bloque {bloque}
                </h2>
                <div className="space-y-2">
                  {lista.map((o) => (
                    <Link key={o.codigo} to={`/campo/avance/${encodeURIComponent(o.codigo)}`}>
                      <Card
                        className={cn(
                          'flex items-center justify-between p-3 transition-transform active:scale-[0.98]',
                          o.estado_operativo === 'en_espera' && 'opacity-60',
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-adelante-primary/15">
                            <Buildings
                              className="h-5 w-5 text-adelante-primary-dark"
                              weight="bold"
                              aria-hidden="true"
                            />
                          </div>
                          <div>
                            <p className="flex items-center gap-1.5 font-mono text-sm font-semibold">
                              {o.codigo}
                              <BadgeVenta estado={o.estado_venta} />
                              {o.estado_operativo === 'en_espera' && (
                                <span className="rounded bg-violet-100 px-1 py-0.5 text-[9px] font-medium text-violet-600">
                                  ❄ Congelada
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {o.nombre_modelo ?? 'Sin modelo'} · {o.tipo_casa ?? 'tipo —'}
                            </p>
                          </div>
                        </div>
                        <CaretRight
                          className="h-4 w-4 text-muted-foreground"
                          weight="bold"
                          aria-hidden="true"
                        />
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ToggleBtn({
  label,
  activo,
  onClick,
  children,
}: {
  label: string;
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded',
        activo ? 'bg-adelante-primary text-adelante-foreground' : 'text-muted-foreground',
      )}
    >
      {children}
    </button>
  );
}
